<script lang="ts">
  import type { Snippet } from 'svelte';
  import { page, router } from '@inertiajs/svelte';
  import { Toaster } from 'svelte-sonner';
  import SignOutConfirmModal from '../components/SignOutConfirmModal.svelte';
  import GlobalSearchModal from '../components/GlobalSearchModal.svelte';
  import { initFavorites, favoriteViews } from '../lib/stores/favorites';
  import { initTaskCopyPreferences } from '../lib/stores/taskCopyPreferences';
  import { DEFAULT_NAVBAR_VIEWS } from '../lib/types';
  import { getTimelinePath } from '../lib/timelinePath';
  import { getGlobalPageDestination, isTimeNavigablePath } from '../lib/pageHotkeys';
  import { navigateToFavorite } from '../lib/favoritesNavigation';
  import { openGlobalSearch } from '../lib/stores/globalSearch';
  import { toggleWorkQueue } from '../lib/stores/ui';
  import {
    initNow,
    isNowBoardPath,
    nowDensity,
    startNowCacheSync,
    toggleNowVisibility,
    nowBoardDisplayMode,
  } from '../lib/stores/now';
  import { initMobileTabs } from '../lib/stores/mobileTabs';
  import { viewport } from '../lib/stores/viewport';
  import NowBar from '../components/NowBar.svelte';
  import BottomTabBar from '../components/mobile/BottomTabBar.svelte';

  interface Props {
    children: Snippet;
  }

  let { children }: Props = $props();

  // Initialize favorites + copy-settings stores from Inertia shared props (centralized here instead of per-page)
  // eslint-disable-next-line momentium/no-unguarded-effect -- Inertia prop sync: centralized favorites/navbar/copy-settings init from shared props
  $effect(() => {
    const prefs = ($page.props as any).preferences;
    if (prefs) {
      initFavorites(
        prefs.favoriteViews || [],
        prefs.navbarConfig || {
          show_default_views: true,
          hidden_default_views: [],
          navbar_views: DEFAULT_NAVBAR_VIEWS,
        },
      );
      initTaskCopyPreferences(prefs.taskCopy);
      initMobileTabs(prefs.mobileTabs);
    }
    initNow(($page.props as any).now);
    startNowCacheSync();
  });

  // Track flash messages with derived visibility.
  // Instead of using $effect to reset show flags when flash changes,
  // we track which message content was dismissed. A new flash with different
  // content automatically shows because it won't match the dismissed value.
  let flash = $derived($page.props.flash as { notice?: string; alert?: string } | undefined);

  let dismissedNotice = $state<string | null>(null);
  let dismissedAlert = $state<string | null>(null);
  let showNotice = $derived(!!flash?.notice && flash.notice !== dismissedNotice);
  let showAlert = $derived(!!flash?.alert && flash.alert !== dismissedAlert);

  function dismissNotice() {
    dismissedNotice = flash?.notice ?? null;
  }

  function dismissAlert() {
    dismissedAlert = flash?.alert ?? null;
  }

  let onNowBoard = $derived(isNowBoardPath($page.url));
  let isLoggedIn = $derived(!!($page.props as any).auth?.user);
  // On phones the Now tab (with its badge) replaces the Now bar.
  let showNowBar = $derived(
    isLoggedIn && !$viewport.isMobile && $nowDensity !== 'hidden' && !onNowBoard,
  );
  let showTabBar = $derived(isLoggedIn && $viewport.isMobile && !$nowBoardDisplayMode);
  // Lift toasts above the tab bar. Sonner switches to `mobileOffset` below 600px,
  // but the tab bar shows up to 767px, so set both.
  let toastOffset = $derived(
    showTabBar ? { bottom: 'calc(var(--tabbar-h) + var(--safe-bottom) + 12px)' } : undefined,
  );

  function handleGlobalKeydown(event: KeyboardEvent) {
    if (
      event.defaultPrevented ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement ||
      (event.target instanceof HTMLElement && event.target.isContentEditable) ||
      document.querySelector('[role="dialog"]') ||
      isNowBoardPath($page.url)
    ) {
      return;
    }

    const key = event.key.toLowerCase();

    if (key === 'k' || event.key === '/') {
      event.preventDefault();
      event.stopImmediatePropagation();
      openGlobalSearch();
      return;
    }

    if (key === 'w') {
      event.preventDefault();
      event.stopImmediatePropagation();
      toggleWorkQueue();
      return;
    }

    // The Now bar never renders on mobile, so N would silently flip a hidden setting.
    if (key === 'n' && !$viewport.isMobile) {
      event.preventDefault();
      event.stopImmediatePropagation();
      toggleNowVisibility();
      return;
    }

    // 1-9 jump to favorite views. Digits are reserved globally — page-level
    // handlers must not bind 1-9 to other actions. Timeline/calendar pages
    // handle these themselves (with richer viewConfig-aware URLs), so skip
    // there to avoid double navigation.
    if (!isTimeNavigablePath($page.url) && /^[1-9]$/.test(key)) {
      const sorted = [...$favoriteViews].sort((a, b) => a.position - b.position);
      const fav = sorted[parseInt(key, 10) - 1];
      if (fav) {
        event.preventDefault();
        event.stopImmediatePropagation();
        navigateToFavorite(fav);
      }
      return;
    }

    const destination = getGlobalPageDestination(
      key,
      getTimelinePath($favoriteViews, { mobile: $viewport.isMobile }),
    );
    if (destination) {
      event.preventDefault();
      event.stopImmediatePropagation();
      router.get(destination);
    }
  }
</script>

<svelte:window onkeydown={handleGlobalKeydown} />

<div class="layout">
  {#if showNowBar}
    <NowBar />
  {/if}

  <div class="layout-body" class:now-board-body={onNowBoard} class:with-tab-bar={showTabBar}>
    <!-- Flash Messages -->
    {#if flash?.notice && showNotice}
      <div class="flash-container">
        <div class="flash flash-notice">
          <span>{flash.notice}</span>
          <button onclick={dismissNotice} class="flash-dismiss" aria-label="Dismiss">×</button>
        </div>
      </div>
    {/if}

    {#if flash?.alert && showAlert}
      <div class="flash-container">
        <div class="flash flash-alert">
          <span>{flash.alert}</span>
          <button onclick={dismissAlert} class="flash-dismiss" aria-label="Dismiss">×</button>
        </div>
      </div>
    {/if}

    {@render children()}
  </div>

  {#if showTabBar}
    <BottomTabBar />
  {/if}
</div>

<GlobalSearchModal />
<SignOutConfirmModal />

<Toaster
  position="bottom-center"
  offset={toastOffset}
  mobileOffset={toastOffset}
  richColors
  visibleToasts={3}
  toastOptions={{
    duration: 7000,
    class: 'app-toast',
  }}
/>

<style>
  .layout {
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
    /* viewport-fit=cover: keep content clear of the notch in landscape */
    padding-inline: var(--safe-left) var(--safe-right);
  }

  .layout-body {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  /* Keep the end of every page scrollable clear of the fixed tab bar. */
  .layout-body.with-tab-bar {
    padding-bottom: calc(var(--tabbar-h) + var(--safe-bottom));
  }

  .layout-body.now-board-body {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  :global([data-sonner-toaster]) {
    --width: min(22rem, calc(100vw - 2rem));
  }

  :global([data-sonner-toast].app-toast),
  :global([data-sonner-toast]) {
    max-width: min(22rem, calc(100vw - 2rem));
  }

  :global([data-sonner-toast] [data-title]),
  :global([data-sonner-toast] [data-description]),
  :global([data-sonner-toast] [data-content]) {
    overflow: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  :global([data-sonner-toast] [data-title]),
  :global([data-sonner-toast] [data-description]) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }

  .flash-container {
    position: fixed;
    top: 1rem;
    right: 1rem;
    z-index: 9999;
    max-width: 400px;
  }

  .flash {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease-out;
  }

  .flash-notice {
    background-color: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #065f46;
  }

  .flash-alert {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #991b1b;
  }

  .flash-dismiss {
    background: none;
    border: none;
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.15s;
  }

  .flash-dismiss:hover {
    opacity: 1;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(1rem);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
</style>
